import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { CRM_PERMISSION_LABELS, type CrmRole, type CrmRolePermissions } from "./types";

const fieldCls =
  "w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm text-ink outline-none focus:border-brand";
const labelCls = "mb-1.5 block text-xs font-semibold text-ink-2";

const BLANK_PERMISSIONS: CrmRolePermissions = { view_all_leads: false, edit_leads: true, delete_leads: false, assign_leads: false };

export function RoleModal({ role, onClose, onSave }: {
  /** Omit to create a new role. */
  role?: CrmRole | null;
  onClose: () => void;
  onSave: (data: { name: string; permissions: CrmRolePermissions }) => Promise<void>;
}) {
  const [name, setName] = useState(role?.name ?? "");
  const [permissions, setPermissions] = useState<CrmRolePermissions>(role?.permissions ?? BLANK_PERMISSIONS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (saving) return;
    const trimmed = name.trim();
    if (!trimmed) { setError("Role name is required."); return; }
    setSaving(true); setError("");
    try {
      await onSave({ name: trimmed, permissions });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save role.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={role ? "Edit role" : "New role"}
      subtitle="Choose exactly what this role can do with leads."
      onClose={onClose}
      width="max-w-lg"
      footer={
        <>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">Cancel</button>
          <button disabled={saving} onClick={submit} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
            {saving ? "Saving…" : "Save role"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Role name *</label>
          <input autoFocus maxLength={100} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Senior Sales Manager" className={fieldCls} />
        </div>

        <div>
          <label className={labelCls}>Leads permissions</label>
          <div className="space-y-2 rounded-lg border border-line p-3">
            {(Object.keys(CRM_PERMISSION_LABELS) as (keyof CrmRolePermissions)[]).map(key => (
              <label key={key} className="flex items-center gap-2.5 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={permissions[key]}
                  onChange={e => setPermissions(prev => ({ ...prev, [key]: e.target.checked }))}
                  className="h-4 w-4 accent-brand"
                />
                {CRM_PERMISSION_LABELS[key]}
              </label>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-ink-3">
            "View all leads" off means this role only sees leads assigned to them.
          </p>
        </div>

        {error && <p role="alert" className="text-xs font-semibold text-red-500">{error}</p>}
      </div>
    </Modal>
  );
}
